import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ScrollToTop } from './components/ScrollToTop';
import { AuthSplitScreen } from './components/AuthSplitScreen';
import { ContributionPlatform } from './components/contributions/ContributionPlatform';
// CuratedIssues stays eager: it's imported by the eager home feed
// (ContributionPlatform), so it's on the hot path and can't leave the main
// bundle anyway. IssueView + the heavy content routes below are lazy().
import { CuratedIssues } from './components/issues/CuratedIssues';
import { AuthenticatedLayout } from './components/layout/AuthenticatedLayout';
import { ContributionsProvider } from './contexts/ContributionsContext';
import { AnalyticsProvider } from './components/AnalyticsProvider';
import { supabase } from './lib/supabase';
import { withTimeout } from './lib/withTimeout';
import { TestScrape } from './test-scrape';
import { FounderAskPaneDemo } from './components/discussions/FounderAskPaneDemo';
import { DiscussRedirect } from './components/discussions/DiscussRedirect';
import './App.css';

// Admin tooling is code-split so the public app doesn't ship it in the main
// bundle; the chunk loads on first visit to an /admin route.
const AdminDashboard = lazy(() => import('./components/admin/AdminDashboard').then(m => ({ default: m.AdminDashboard })));
const BrandDetailPage = lazy(() => import('./components/admin/BrandDetailPage').then(m => ({ default: m.BrandDetailPage })));
const ItemDetailPage = lazy(() => import('./components/admin/ItemDetailPage').then(m => ({ default: m.ItemDetailPage })));
const UserDetailPage = lazy(() => import('./components/admin/UserDetailPage').then(m => ({ default: m.UserDetailPage })));
const AdminAddContent = lazy(() => import('./components/admin/AdminAddContent').then(m => ({ default: m.AdminAddContent })));
const AdminCurationEditor = lazy(() => import('./components/admin/AdminCurationEditor').then(m => ({ default: m.AdminCurationEditor })));
const GuestProfilePage = lazy(() => import('./components/admin/GuestProfilePage').then(m => ({ default: m.GuestProfilePage })));

// Public issue view — code-split to keep react-grid-layout out of the main bundle.
const IssueView = lazy(() => import('./components/issues/IssueView').then(m => ({ default: m.IssueView })));

// Static / legal / auth pages — rarely on the hot path, each self-contained, so
// code-split them out of the main bundle (loaded on first visit to their route).
const SupabaseTest = lazy(() => import('./components/SupabaseTest').then(m => ({ default: m.SupabaseTest })));
const ResetPassword = lazy(() => import('./pages/ResetPassword').then(m => ({ default: m.ResetPassword })));
const SetupAccount = lazy(() => import('./pages/SetupAccount').then(m => ({ default: m.SetupAccount })));
const Unsubscribe = lazy(() => import('./pages/Unsubscribe').then(m => ({ default: m.Unsubscribe })));
const Preferences = lazy(() => import('./pages/Preferences').then(m => ({ default: m.Preferences })));
const EmailView = lazy(() => import('./pages/EmailView').then(m => ({ default: m.EmailView })));
const About = lazy(() => import('./pages/About').then(m => ({ default: m.About })));
const Terms = lazy(() => import('./pages/Terms').then(m => ({ default: m.Terms })));
const Privacy = lazy(() => import('./pages/Privacy').then(m => ({ default: m.Privacy })));
const Support = lazy(() => import('./pages/Support').then(m => ({ default: m.Support })));
const AccountSuspended = lazy(() => import('./pages/AccountSuspended').then(m => ({ default: m.AccountSuspended })));
const ClaimAccountPage = lazy(() => import('./components/auth/ClaimAccountPage').then(m => ({ default: m.ClaimAccountPage })));

// Heavy content routes — none on the first-paint critical path (home feed +
// login stay eager). CuratedIssues is intentionally NOT here (see import note).
const ProductDetail = lazy(() => import('./components/products/ProductDetail').then(m => ({ default: m.ProductDetail })));
const CurationDetail = lazy(() => import('./components/curations/CurationDetail').then(m => ({ default: m.CurationDetail })));
const SmartProfileRoute = lazy(() => import('./components/curations/SmartProfileRoute').then(m => ({ default: m.SmartProfileRoute })));
const BrandPage = lazy(() => import('./components/brands/BrandPage').then(m => ({ default: m.BrandPage })));
const DiscussionsPage = lazy(() => import('./components/discussions/DiscussionsPage').then(m => ({ default: m.DiscussionsPage })));
const IssuePreview = lazy(() => import('./components/issues/IssuePreview').then(m => ({ default: m.IssuePreview })));

function AppContent() {
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const handleAuthComplete = () => {
    // Handled by auth state change
  };

  // Official Supabase auth pattern from documentation
  useEffect(() => {
    // Get initial session — skip auto-login on the reset-password page
    const isRecoveryFlow = window.location.pathname === '/reset-password';

    // Absolute fallback: never leave the loading spinner up for more than 8s,
    // even if every promise below hangs. The user can sign in fresh.
    const loadingFallback = setTimeout(() => setLoading(false), 8_000);

    const applySession = async (session: any) => {
      if (!session) {
        setCurrentUser(null);
        return;
      }
      try {
        const { data: profile } = await withTimeout(
          supabase
            .from('users')
            .select('is_suspended')
            .eq('id', session.user.id)
            .maybeSingle(),
          5_000,
          'Suspension check',
        );

        if (profile?.is_suspended) {
          await supabase.auth.signOut();
          setCurrentUser(null);
          // Redirect to the suspended-account page so the user knows what
          // happened and how to appeal.
          if (window.location.pathname !== '/account-suspended') {
            window.location.assign('/account-suspended');
          }
          return;
        }
      } catch (err) {
        // If the lookup fails (RLS, network, timeout), fall through and let the
        // user in rather than locking them out. Layer-2 RLS still blocks writes.
        console.error('Suspension check failed:', err);
      }
      setCurrentUser(session.user);
    };

    withTimeout(supabase.auth.getSession(), 5_000, 'Session restore')
      .then(async (result) => {
        const session = result?.data?.session ?? null;
        try {
          if (session && !isRecoveryFlow) {
            await applySession(session);
          }
        } finally {
          clearTimeout(loadingFallback);
          setLoading(false);
        }
      })
      .catch((err) => {
        console.error('Session restore failed:', err);
        clearTimeout(loadingFallback);
        setLoading(false);
      });

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      // Don't set authenticated user during password recovery — let ResetPassword page handle it
      if (event === 'PASSWORD_RECOVERY') {
        return;
      }
      await applySession(session);
    });

    return () => {
      clearTimeout(loadingFallback);
      subscription.unsubscribe();
    };
  }, []);

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F2F0E5' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Suspense
        fallback={
          <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F2F0E5' }}>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
          </div>
        }
      >
      <Routes>
      {/* Admin routes - must come before profile routes to avoid conflicts */}
      <Route
        path="/admin"
        element={
          currentUser ? (
            <AdminDashboard userId={currentUser.id} onBack={() => navigate('/')} />
          ) : (
            <Navigate to="/" />
          )
        }
      >
        {/* Engagement detail pages render inside the AdminDashboard shell via <Outlet/> */}
        <Route path="insights/brand/:brand" element={<BrandDetailPage />} />
        <Route path="insights/item/:itemId" element={<ItemDetailPage />} />
        <Route path="insights/user/:userId" element={<UserDetailPage />} />
        {/* Section pages render inside AdminDashboard, which derives the
            active section from the URL. */}
        <Route path=":section" element={null} />
      </Route>

      {/* Admin Add Content */}
      <Route
        path="/admin/content/add"
        element={
          currentUser ? (
            <AdminAddContent userId={currentUser.id} />
          ) : (
            <Navigate to="/" />
          )
        }
      />

      {/* Issue editor routes */}
      <Route
        path="/admin/issues/:id/edit"
        element={
          currentUser ? (
            <AdminCurationEditor userId={currentUser.id} />
          ) : (
            <Navigate to="/" />
          )
        }
      />

      {/* Chrome-less curation render for the editor's Preview mode: the real
          public page without the global nav or action bar */}
      <Route
        path="/embed/curation/:username/:slug"
        element={<CurationDetail embed />}
      />

      {/* Guest curator profile: create + edit (order matters — /new first) */}
      <Route
        path="/admin/guests/new"
        element={currentUser ? <GuestProfilePage mode="create" /> : <Navigate to="/" />}
      />
      <Route
        path="/admin/guests/:username"
        element={currentUser ? <GuestProfilePage mode="edit" /> : <Navigate to="/" />}
      />

      {/* Issue preview route */}
      <Route path="/issues/preview/:slug" element={<IssuePreview />} />
      
      {/* Test routes */}
      <Route path="/test" element={<SupabaseTest />} />
      <Route path="/test-scrape" element={<TestScrape />} />
      {process.env.NODE_ENV === 'development' && (
        <Route path="/test-pane" element={<FounderAskPaneDemo />} />
      )}
      
      {/* Password reset route */}
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* Guest claim: where the magic link lands to set a password or connect
          Google/Apple. Public route — it establishes its own session. */}
      <Route path="/account/setup" element={<SetupAccount />} />

      {/* Legacy/expected admin URLs */}
      <Route path="/review/users" element={<Navigate to="/admin/users" replace />} />
      <Route path="/review" element={<Navigate to="/admin" replace />} />

      {/* Guest account claim route - accessible without auth */}
      <Route path="/claim/:token" element={<ClaimAccountPage />} />

      {/* Email preference routes - accessible without auth */}
      <Route path="/unsubscribe" element={<Unsubscribe />} />
      <Route path="/preferences" element={<Preferences />} />
      <Route path="/email/:id" element={<EmailView />} />

      {/* Static pages - accessible without auth */}
      <Route path="/about" element={<About />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/support" element={<Support />} />
      <Route path="/account-suspended" element={<AccountSuspended />} />

      {/* Authenticated routes with shared layout */}
      {currentUser && (
        <Route element={<AuthenticatedLayout user={currentUser} />}>
          <Route path="/" element={<ContributionPlatform user={currentUser} />} />
          {/* One shared element for both paths: guests get the adaptive hub
              page with inline curation swapping; others get the classic split */}
          <Route path="/profile/:username" element={<SmartProfileRoute />} />
          <Route path="/profile/:username/issues/:slug" element={<SmartProfileRoute />} />
          <Route path="/brand/:brandname" element={<BrandPage />} />
          <Route path="/product/:id" element={<ProductDetail currentUserId={currentUser.id} />} />
          <Route path="/issues/:slug" element={<IssueView />} />
          <Route path="/curations" element={<CuratedIssues />} />
          {/* iOS shared curations as /curations/:slug; IssueView resolves the slug and redirects to the canonical profile URL. */}
          <Route path="/curations/:slug" element={<IssueView />} />
          {/* Backwards-compat redirect from the previous /curated route. */}
          <Route path="/curated" element={<Navigate to="/curations" replace />} />
          <Route path="/discussions" element={<DiscussionsPage />} />
          <Route path="/discussions/:type/:targetId" element={<DiscussionsPage />} />
          {/* iOS shares founder-ask links as /discuss/:askId — redirect to the canonical thread route. */}
          <Route path="/discuss/:askId" element={<DiscussRedirect />} />
          <Route path="/contribute" element={<ContributionPlatform user={currentUser} />} />
          <Route path="/brands" element={<ContributionPlatform user={currentUser} />} />
        </Route>
      )}

      {/* Catch-all for authenticated users - unknown URLs go home */}
      {currentUser && <Route path="*" element={<Navigate to="/" replace />} />}

      {/* Catch-all for unauthenticated users - must come last */}
      {!currentUser && (
        <Route path="*" element={<AuthSplitScreen onComplete={handleAuthComplete} />} />
      )}
      </Routes>
      </Suspense>
    </>
  );
}

// One shared query cache for the app. staleTime keeps recently-fetched data
// fresh so navigating back to a page renders instantly from cache instead of
// refetching; refetchOnWindowFocus stays off so tabbing back never reloads.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <AnalyticsProvider>
          {/* One cache for the whole app. A single ContributionsProvider above
              every route tree means the Discover feed survives an /admin
              round-trip (previously each tree mounted its own, wiping it). */}
          <ContributionsProvider>
            <AppContent />
          </ContributionsProvider>
        </AnalyticsProvider>
      </Router>
    </QueryClientProvider>
  );
}

export default App;